import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/pages/login.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/',
      component: () => import('@/layouts/default.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/pages/dashboard.vue'),
        },
        {
          path: 'mpesa',
          name: 'mpesa',
          component: () => import('@/pages/mpesa/index.vue'),
        },
        {
          path: 'settings',
          name: 'settings',
          redirect: '/settings/vehicles',
        },
        {
          path: 'settings/vehicles',
          name: 'settings-vehicles',
          component: () => import('@/pages/vehicles/index.vue'),
        },
        {
          path: 'settings/investors',
          name: 'settings-investors',
          component: () => import('@/pages/settings/investors.vue'),
        },
        {
          path: 'settings/crew',
          name: 'settings-crew',
          component: () => import('@/pages/crew/index.vue'),
        },
        {
          path: 'settings/allocations',
          name: 'settings-allocations',
          component: () => import('@/pages/settings/allocations.vue'),
        },
        {
          // Retired: target templates live under Settings → Allocation
          // Buckets now, below the buckets they set defaults for.
          path: 'settings/vehicle-targets',
          redirect: '/settings/allocations',
        },
        {
          // A read-only view of a day that already happened — it lives with
          // the other reports now.
          path: 'allocation/daily-accounts',
          redirect: '/reports/daily-accounts',
        },
        {
          // Trip estimates are derived from M-Pesa patterns and carry no
          // actions — they read as a report, so they live under one.
          path: 'trips',
          redirect: '/reports/trips',
        },
        {
          // Retired: crew assignment happens on Settings → Vehicles now, on
          // the row that already shows the crew. Redirected rather than
          // dropped so a bookmark or an old link still lands somewhere.
          path: 'settings/assignments',
          redirect: '/settings/vehicles',
        },
        {
          path: 'settings/permission-templates',
          name: 'settings-permission-templates',
          component: () => import('@/pages/settings/permission-templates.vue'),
        },
        {
          path: 'settings/f1-stage-fees',
          name: 'settings-f1-stage-fees',
          component: () => import('@/pages/settings/f1-stage-fees.vue'),
          meta: { permission: 'f1.view' },
        },
        {
          path: 'settings/salary-tiers',
          name: 'settings-salary-tiers',
          component: () => import('@/pages/settings/salary-tiers.vue'),
          meta: { permission: 'salary.view' },
        },
        {
          path: 'salary/payroll',
          name: 'salary-payroll',
          component: () => import('@/pages/salary/payroll.vue'),
          meta: { permission: 'salary.view' },
        },
        {
          path: 'salary/earnings',
          name: 'salary-earnings',
          component: () => import('@/pages/salary/earnings.vue'),
          meta: { permission: 'salary.view' },
        },
        {
          path: 'salary/top-ups',
          name: 'salary-top-ups',
          component: () => import('@/pages/salary/top-ups.vue'),
          meta: { permission: 'salary.view' },
        },
        // Deductions — money charged against a vehicle-day out in the field,
        // by a pump attendant or a stage marshal. Both hold that day's bonus
        // until they are answered, which is why they sit together.
        {
          path: 'fuel/requests',
          name: 'fuel-requests',
          component: () => import('@/pages/fuel/requests.vue'),
          // Matches the API's read gate on GET /fuel/requests.
          meta: { permission: 'fuel.view' },
        },
        {
          // One vehicle's fuel account: the balance, the statement, and the
          // records that move it. A ROUTE rather than the modal it replaces —
          // it has a back button, a shareable URL, and room for the figures,
          // and the attendant shell mounts the same component under its own
          // guarded path below.
          path: 'fuel/vehicles/:vehicleId',
          name: 'fuel-vehicle',
          component: () => import('@/pages/fuel/vehicles/[vehicleId].vue'),
          // Matches the API's gate on GET /fuel/vehicles/<id>/ledger.
          meta: { permission: 'fuel.view' },
        },
        // The Fuel Module reports — management's own four views of the same
        // fuel money (Antony's guide, Aug 2026). FOUR routes rather than four
        // tabs of one page: each is a thing you send somebody a link to, and
        // the summary's rows lead into the ledger, which a URL has to be able
        // to say. The strip at the top of each keeps them reading as one
        // surface and carries the period across.
        //
        // All four are presentation over endpoints that already exist, so they
        // sit behind the same `fuel.view` the API gates those reads on.
        {
          path: 'fuel/reports',
          redirect: { name: 'fuel-report-summary' },
        },
        {
          path: 'fuel/reports/summary',
          name: 'fuel-report-summary',
          component: () => import('@/pages/fuel/reports/summary.vue'),
          meta: { permission: 'fuel.view' },
        },
        {
          path: 'fuel/reports/ledger',
          name: 'fuel-report-ledger',
          component: () => import('@/pages/fuel/reports/ledger.vue'),
          meta: { permission: 'fuel.view' },
        },
        {
          path: 'fuel/reports/veripay',
          name: 'fuel-report-veripay',
          component: () => import('@/pages/fuel/reports/veripay.vue'),
          meta: { permission: 'fuel.view' },
        },
        {
          path: 'fuel/reports/owner',
          name: 'fuel-report-owner',
          component: () => import('@/pages/fuel/reports/owner.vue'),
          meta: { permission: 'fuel.view' },
        },
        {
          // The fifth report, and the only one that ACTS: the station's own
          // account — what has been paid to each forecourt and what is still
          // owed. Read-only for a partner, the two settlement verbs for the
          // sacco. Behind the same `fuel.view` as the other four: everything
          // it READS is gated on that, and each verb is gated separately in
          // the page on the permission its own endpoint requires.
          path: 'fuel/reports/payments',
          name: 'fuel-report-payments',
          component: () => import('@/pages/fuel/reports/payments.vue'),
          meta: { permission: 'fuel.view' },
        },
        {
          // The sixth: money paid against a vehicle's fuel balance, fleet-wide.
          // Its own screen rather than a tab of the Owner Report, because it is
          // the one fuel screen that is not about a day's fuel at all — and
          // because the office opens it to key a payment, not to read one.
          // Behind `fuel.view` like the rest; each verb on it is gated in the
          // page on the permission its own endpoint requires.
          path: 'fuel/reports/other-payments',
          name: 'fuel-report-other-payments',
          component: () => import('@/pages/fuel/reports/other-payments.vue'),
          meta: { permission: 'fuel.view' },
        },
        {
          // The charges QUEUE. Settings → F1 Stage Fees keeps the price list;
          // this is what needs answering today.
          path: 'f1/charges',
          name: 'f1-charges',
          component: () => import('@/pages/f1/charges.vue'),
          meta: { permission: 'f1.view' },
        },
        {
          path: 'wallets',
          name: 'wallets',
          component: () => import('@/pages/wallets/index.vue'),
        },
        {
          path: 'central-wallet',
          name: 'central-wallet',
          component: () => import('@/pages/central-wallet/index.vue'),
        },
        {
          path: 'passenger-transfers',
          name: 'passenger-transfers',
          component: () => import('@/pages/passenger-transfers/index.vue'),
        },
        {
          path: 'wallets/:userId',
          name: 'wallet-detail',
          component: () => import('@/pages/wallets/[userId].vue'),
        },
        {
          path: 'wallets/sacco-bank/:bankAccountId',
          name: 'wallet-sacco-bank-detail',
          component: () => import('@/pages/wallets/sacco-bank/[bankAccountId].vue'),
        },
        {
          path: 'disbursements',
          name: 'disbursements',
          component: () => import('@/pages/disbursements/index.vue'),
        },
        {
          path: 'approvals',
          name: 'approvals',
          component: () => import('@/pages/approvals/index.vue'),
        },
        {
          path: 'ma3club',
          name: 'ma3club-operations',
          component: () => import('@/pages/ma3club/operations.vue'),
          meta: { permission: 'ma3club.booking.view' },
        },
        {
          path: 'ma3club/trip',
          name: 'ma3club-trip',
          component: () => import('@/pages/ma3club/trip.vue'),
          meta: { permission: 'ma3club.booking.view' },
        },
        {
          path: 'ma3club/earnings',
          name: 'ma3club-earnings',
          component: () => import('@/pages/ma3club/earnings.vue'),
          meta: { permission: 'ma3club.settlement.view' },
        },
        {
          path: 'ma3club/schedule',
          name: 'ma3club-schedule',
          component: () => import('@/pages/ma3club/schedule.vue'),
          meta: { permission: 'ma3club.booking.view' },
        },
        {
          // The sacco's record of platform support visits. Gated on the same
          // permission the API enforces — it sits on the SACCO_ADMIN floor,
          // so an admin reaches it with no grant, a manager only if given it.
          path: 'support-access',
          name: 'support-access',
          component: () => import('@/pages/support-access/index.vue'),
          meta: { permission: 'support.session.view' },
        },
        {
          path: 'reports',
          redirect: '/reports/daily-performance',
        },
        {
          path: 'reports/daily-performance',
          name: 'reports-daily-performance',
          component: () => import('@/pages/reports/daily-performance.vue'),
        },
        // Folded into the tabbed Performance page — old links land on the
        // right tab.
        {
          path: 'reports/fuel-performance',
          redirect: '/reports/daily-performance?tab=fuel',
        },
        {
          path: 'reports/custom',
          redirect: '/reports/daily-performance',
        },
        {
          path: 'reports/daily-accounts',
          name: 'reports-daily-accounts',
          component: () => import('@/pages/allocation/daily-accounts.vue'),
        },
        {
          path: 'reports/trips',
          name: 'reports-trips',
          component: () => import('@/pages/trips/index.vue'),
        },
        {
          path: 'reports/vehicle-owners',
          name: 'reports-vehicle-owners',
          component: () => import('@/pages/reports/vehicle-owners.vue'),
        },
        {
          path: 'reports/crew-sacco',
          name: 'reports-crew-sacco',
          component: () => import('@/pages/reports/crew-sacco.vue'),
        },
        {
          path: 'reports/wallet-statement',
          name: 'reports-wallet-statement',
          component: () => import('@/pages/reports/wallet-statement.vue'),
        },
        {
          path: 'reports/wallet-statement/:userId',
          name: 'reports-wallet-statement-detail',
          component: () => import('@/pages/reports/wallet-statement-detail.vue'),
        },
      ],
    },
    // ── the forecourt ────────────────────────────────────────────────────
    //
    // A fuel attendant is a partner at a station, not sacco staff, and this
    // is the whole of the app they see: a handful of screens under their own
    // guarded paths. `meta.attendant` is what the guard reads — membership of
    // this subtree, not a path prefix, so the two shells cannot be confused by
    // a route that moves.
    //
    // ONE layout, the sacco's (John, 2026-08-25: "we should not have two
    // application shells"). The forecourt had a second copy of the same rail,
    // the same card and the same mobile drawer, which only bought drift — a
    // fix to the header had to be made twice. The rail's ENTRIES still differ:
    // `Sidebar.vue` picks its navigation off `portalAccess`, and the page
    // header's account cluster does the same. The subtree stays separate
    // because the GUARD is membership of it.
    {
      path: '/station',
      component: () => import('@/layouts/default.vue'),
      meta: { requiresAuth: true, attendant: true },
      children: [
        {
          path: '',
          name: 'station',
          component: () => import('@/pages/station/index.vue'),
          // Matches the API's read gate — an attendant holds fuel.view on
          // their floor, so this is a floor, not a grant they might lack.
          meta: { permission: 'fuel.view' },
        },
        {
          // The fills recorded at this forecourt. A route of its own so the
          // shell's sidebar (which lives in the layout, and so survives every
          // navigation) can point at it and light up correctly — as a local
          // tab on the home page it vanished the moment the report opened.
          path: 'fills',
          name: 'station-fills',
          component: () => import('@/pages/station/fills.vue'),
          meta: { permission: 'fuel.view' },
        },
        {
          // The forecourt's own report — the fuel columns of the day sheet,
          // one day at a time, organized like the sacco's Reports →
          // Performance page so both ends of a phone call read the same
          // screen. Its own route rather than a view of the home screen: it
          // answers a different question (the day's sheet, not the car at the
          // pump), and a URL means it can be linked to and backed out of.
          //
          // Gated on the permission the API gates the read on — floor-only
          // for FUEL_ATTENDANT, so an attendant always has it and nobody else
          // can be given it. Sacco users reach the same report through
          // Reports → Performance, which is unchanged.
          path: 'report',
          name: 'station-report',
          component: () => import('@/pages/station/report.vue'),
          meta: { permission: 'fuel.report.view' },
        },
        // The four Fuel Module reports, in the forecourt's own shell. The SAME
        // page components the sacco reaches under /fuel/reports/… — one screen
        // with one set of figures, so the two ends of a phone call are looking
        // at the same thing; only the chrome around them differs (the
        // components branch on the shell, never on capability).
        //
        // Their own paths rather than the sacco's, because the guard is
        // membership of this subtree: a partner reaching /fuel/reports/summary
        // would be bounced, and a sacco admin must not land here.
        //
        // Gated on `fuel.view` — the permission the API gates every read
        // behind them on, and one an attendant floors.
        {
          path: 'ledger',
          name: 'station-ledger',
          component: () => import('@/pages/fuel/reports/ledger.vue'),
          meta: { permission: 'fuel.view' },
        },
        {
          path: 'summary',
          name: 'station-summary',
          component: () => import('@/pages/fuel/reports/summary.vue'),
          meta: { permission: 'fuel.view' },
        },
        {
          path: 'veripay',
          name: 'station-veripay',
          component: () => import('@/pages/fuel/reports/veripay.vue'),
          meta: { permission: 'fuel.view' },
        },
        {
          path: 'owner',
          name: 'station-owner',
          component: () => import('@/pages/fuel/reports/owner.vue'),
          meta: { permission: 'fuel.view' },
        },
        {
          // The prototype's Settlement group, in the forecourt's shell. A
          // partner READS it — the tab and the invoices are station-scoped by
          // the server, and what the sacco owes them is a conversation they
          // are entitled to have — while both verbs on it are the sacco's and
          // never render here.
          path: 'payments',
          name: 'station-payments',
          component: () => import('@/pages/fuel/reports/payments.vue'),
          meta: { permission: 'fuel.view' },
        },
        {
          // Beside the station's own account, in the forecourt's shell: the
          // SAME component the sacco reaches at /fuel/reports/other-payments.
          // A partner READS it — recording is theirs, and taking a payment back
          // off a balance is the sacco's, so those actions never render here.
          path: 'other-payments',
          name: 'station-other-payments',
          component: () => import('@/pages/fuel/reports/other-payments.vue'),
          meta: { permission: 'fuel.view' },
        },
        {
          // The SAME page component the sacco reaches at /fuel/vehicles/:id.
          // The guard decides who may be here (this one carries the shell's
          // `attendant` meta from its parent); the component adapts what it
          // shows. Sharing it is what keeps the balance, the statement and the
          // wording identical for the two people discussing them on a phone
          // call — and every action on it is gated on the API's permission,
          // not on which route rendered it.
          path: 'vehicles/:vehicleId',
          name: 'station-vehicle',
          component: () => import('@/pages/fuel/vehicles/[vehicleId].vue'),
          meta: { permission: 'fuel.view' },
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
  ],
})

// Navigation guard
router.beforeEach((to, _from, next) => {
  const authStore = useAuthStore()
  // Membership of the attendant subtree, read off the matched records so a
  // child route inherits it.
  const isAttendantRoute = to.matched.some((r) => r.meta.attendant === true)
  const access = authStore.portalAccess
  /** Where a refused navigation lands for THIS session — an attendant sent to
   *  the dashboard would bounce straight back here, forever. */
  // The forecourt lands on the day sheet — the screen the shift works from
  // (Vehicles lost its nav entry when the rail took the prototype's shape).
  const home = access === 'attendant' ? { name: 'station-report' } : { name: 'dashboard' }

  if (to.meta.requiresAuth !== false && !authStore.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } })
  } else if (to.name === 'login' && authStore.isAuthenticated) {
    next(home)
  } else if (to.meta.requiresAuth !== false && authStore.isAuthenticated && access === 'none') {
    // Neither shell fits this group: sign them out rather than leave them on
    // a page whose every request the API would refuse.
    authStore.logout()
    next({ name: 'login', query: { error: 'unauthorized' } })
  } else if (access === 'attendant' && !isAttendantRoute) {
    // The forecourt reaches its own routes and nothing else. The API scopes
    // its own reads — this only keeps the partner off pages written for the
    // sacco, which would render empty or refused.
    next({ name: 'station' })
  } else if (access === 'full' && isAttendantRoute) {
    // And the sacco does not land on the forecourt's home by accident. The
    // page can share components; the guard decides who sees it.
    next({ name: 'dashboard' })
  } else if (to.meta.permission && authStore.isAuthenticated) {
    const perms = authStore.user?.permissions ?? []
    const required = to.meta.permission as string
    if (perms.includes('*') || perms.includes(required)) {
      next()
    } else if (to.name === home.name) {
      // The home route itself is gated and this session lacks it. Redirecting
      // home would loop; let the page render its own refusal instead.
      next()
    } else {
      next(home)
    }
  } else {
    next()
  }
})

export default router
